import { h } from '@stencil/core';
import { FormField } from '../forms-ui-form-faas.interface';
import { CascadeTree } from './cascade-tree/cascade-tree';
import { CascadeNode } from './cascade-tree/cascade-node';
import { postRenderRefHandler, VariableProps } from './render-fields';

export class CascadeService {
	/** CascadeTree instance for managing the tree structure */
	private tree: CascadeTree = new CascadeTree();

	/** Holds all associated props for form fields  */
	private cascadeProps: FormField[] = [];

	/** Holds dropdown elements for the cascade tree */
	private cascadeDropdownsElement: HTMLFormsUiSelectElement[] = [];

	/** Reference callback for dropdowns */
	private dropdownRef: (elem: HTMLFormsUiSelectElement) => void;

	/**
	 * Constructor to initialize the service and build the tree
	 * @param formFields - All form fields provided to the form
	 * @param cascadeGroup - The group identifier for cascading fields
	 * @param addRef - Function to add references for rendered fields
	 */
	constructor(
		formFields: FormField[],
		cascadeGroup: string,
		addRef: Function,
	) {
		this.dropdownRef = (elem: HTMLFormsUiSelectElement) => {
			if (addRef) addRef(elem); // Add element to parent's formRefs
			this.cascadeDropdownsElement.push(elem); // Track dropdown elements
		};
		this.buildTree(formFields, cascadeGroup); // Build the tree for the cascade group
		this.setCascadeProps(formFields, cascadeGroup); // Sets the props for the cascade dropdown selects
	}

	/**
	 * Builds the tree based on the form fields belonging to the specified cascade group
	 * @param formFields - All form fields provided to the form
	 * @param cascadeGroup - The group identifier for cascading fields
	 */
	private buildTree(formFields: FormField[], cascadeGroup: string) {
		formFields.forEach((field) => {
			if (field.control === 'Dropdown' && field.cascadeGroup === cascadeGroup) {
				this.tree.addNode(field); // Add nodes to the tree
			}
		});
	}

	/**
	 * Sets the cascade props based to be used on the custom select
	 * @param formFields - All form fields provided to the form
	 * @param cascadeGroup - The group identifier for cascading fields
	 */
	private setCascadeProps(formFields: FormField[], cascadeGroup: string) {
		formFields.forEach((field) => {
			if (field.control === 'Dropdown' && field.cascadeGroup === cascadeGroup) {
				this.cascadeProps.push(field); // Store fields for generating dropdown props
			}
		});
	}

	/**
	 * Loops through levels and renders dropdowns for each level
	 */
	renderAllDropdowns(): HTMLDivElement {
		const maxCascadeRank = this.tree.getTreeMaxRank();
		if (maxCascadeRank < 0) return null; // Return null if no dropdowns need to be rendered

		// Generate dropdown elements for each rank
		return (
			<div>
				{Array.from({ length: maxCascadeRank + 1 }).map((_, rank) => this.renderCascadeDropdowns(rank))}
			</div>
		);
	}

	/**
	 * Renders dropdowns based on options return from the tree structure at each cascading levels
	 * @param cascadeRank - The rank or level in the cascade tree
	 * @returns JSX.Element | null - The rendered dropdown or null
	 */
	renderCascadeDropdowns(cascadeRank: number): HTMLDivElement {
		const optionsAtRank = this.tree.getOptionsAtRank(cascadeRank);
		return this.renderSelect(optionsAtRank, cascadeRank);
	}

	private renderSelect(
		optionsAtRank: CascadeNode[],
		cascadeRank: number,
	): HTMLDivElement {
		if (!optionsAtRank || optionsAtRank.length === 0) return null;

		const hasGroups = optionsAtRank.some((option) => option.children && option.children.length > 0);

		// Use the cascadeProps to populate the variable props
		const field = this.cascadeProps[cascadeRank];
		const props: VariableProps = {
			label: field.label || '',
			name: field.name || '',
			required: field.required || false,
			helperText: field.additionalInformation?.additionalText || '',
			'data-element': field.dataElement || '',
			hide: field.type === 'Hidden',
		};
		return (
			<forms-ui-select {...props} ref={(elem) => postRenderRefHandler(elem, field, this.dropdownRef)}>
				{hasGroups
					? optionsAtRank.map((group) => (
						<forms-ui-select-option-group
							key={group.name}
							label={group.label}
							name={group.name}
						>
							{this.renderOptions(group.children, cascadeRank, group.defaultText)}
						</forms-ui-select-option-group>
					))
					: this.renderOptions(optionsAtRank, cascadeRank)}
			</forms-ui-select>
		);
	}

	/**
	 * Helper to render options for dropdowns, including handling group options
	 */
	private renderOptions(
		options: CascadeNode[],
		cascadeRank: number,
		defaultText?: string | null,
	) {
		return options.map((option) => (
			<forms-ui-select-option
				key={option.value}
				label={option.label}
				value={option.value}
				selected={
					defaultText
						? option.value === defaultText
						: option.value === option.defaultText
				}
				onFormsUISelectOptionSelected={async () => {
					await this.handleSelectedOptions(cascadeRank, option.value);
				}}
			></forms-ui-select-option>
		));
	}

	/**
	 * Handles de-selection of an option of a subsequent dropdown
	 * @param currentRank - The rank or level of a cascade dropdown
	 */
	private async deSelectCascadeSelectedOption(cascadeRank: number) {
		const cascadeDropdown = this.cascadeDropdownsElement[cascadeRank];
		if (!cascadeDropdown) {
			// eslint-disable-next-line no-console
			console.error(`Cascade dropdown at rank ${cascadeRank} not found.`);
			return;
		}
		const input = cascadeDropdown.querySelector('input');
		if (input) {
			input.value = ''; // Clear the visible input value
		}
		await cascadeDropdown.resetSelectOptionState();
	}

	/** Helper method to check if the selected value differs from the previous selected value */
	private hasSelectChanged(cascadeRank: number, selectedValue: string): boolean {
		const previousDropdown = this.cascadeDropdownsElement[cascadeRank];
		if (!previousDropdown) return false;
		return previousDropdown.value !== selectedValue;
	}

	/**
	 * Handles option selection, filters options, and updates reactive state
	 * @param cascadeRank - The rank or level of a cascade dropdown
	 * @param selectedValue - The selected value of the dropdown
	 */
	private async handleSelectedOptions(cascadeRank: number, selectedValue: string) {
		const filteredOptions = this.tree.filterByKeyAndRank(selectedValue, cascadeRank);
		// Pass cascade rank and filtered options directly to repopulate/update the cascade dropdown
		await this.updateCascadeDropdowns(cascadeRank, filteredOptions, selectedValue);
	}

	/** Handles updating the cascade dropdown options, selected and focus values */
	async updateCascadeDropdowns(cascadeRank: number, filteredOptions: CascadeNode[], selectedValue: string) {
		const maxCascadeRank = this.tree.getTreeMaxRank();
		const updatePromises = [];
		// Loop through ranks starting from the current rank + 1
		for (let subsequentRank = cascadeRank + 1; subsequentRank <= maxCascadeRank; subsequentRank += 1) {
			const cascadeDropdown = this.cascadeDropdownsElement[subsequentRank];

			if (!cascadeDropdown) {
				// eslint-disable-next-line no-console
				console.error(`Cascade dropdown select for rank ${subsequentRank} not found.`);
				break;
			}
			// If no options exist at this rank, stop further processing
			if (!filteredOptions || filteredOptions.length === 0) {
				// eslint-disable-next-line no-console
				console.error(`No options found for rank ${subsequentRank}, dropdowns.`);
				break;
			}

			// Populate the dropdowns with groups and options
			const filterOptions = filteredOptions.map((group) => {
				const newGroup = document.createElement('forms-ui-select-option-group');
				newGroup.setAttribute('key', group.name);
				newGroup.setAttribute('label', group.label);
				newGroup.setAttribute('name', group.name);

				// Append children options to the group
				group.children.forEach((option) => {
					const newOption = document.createElement('forms-ui-select-option');
					newOption.setAttribute('key', option.value);
					newOption.setAttribute('label', option.label);
					newOption.setAttribute('value', option.value);

					// Attach the event listener to trigger the handleSelectedOptions
					newOption.addEventListener('formsUISelectOptionSelected', async () => {
						await this.handleSelectedOptions(subsequentRank, option.value);
					});

					newGroup.appendChild(newOption);
				});

				return newGroup;
			});

			// Add the promise to update options for the dropdown
			updatePromises.push(cascadeDropdown.updateOptions(filterOptions));

			if (this.hasSelectChanged(cascadeRank, selectedValue)) {
				updatePromises.push(this.deSelectCascadeSelectedOption(subsequentRank));
			}

			// Update filteredOptions for the next rank
			filteredOptions = filteredOptions.flatMap((group) => group.children || []);
		}

		// Wait for all dropdown updates to complete
		await Promise.all(updatePromises);
	}
}
